import * as THREE from 'three';
import ZoomButtons from './ZoomButtons';

// Custom control class to handle zoom button interactions in a Three.js scene
export default class ZoomButtonsControls extends THREE.EventDispatcher {
    constructor(camera, domElement, controls) {
        super();
        this._camera = camera;             // Three.js camera
        this.domElement = domElement;      // DOM element to attach event listeners
        this._controls = controls;         // OrbitControls or any camera control interface
        this._buttons = new ZoomButtons(); // Object3D that contains zoom in/out buttons

        // Bind event handlers to maintain context
        this._handleMouseMove = this._handleMouseMove.bind(this);
        this._handleMouseClick = this._handleMouseClick.bind(this);

        this._listen(); // Start listening to events
    }

    // Attach mouse event listeners to the DOM element
    _listen() {
        this.domElement.addEventListener('mousemove', this._handleMouseMove); // For hover effects
        this.domElement.addEventListener('click', this._handleMouseClick);    // For click interactions
    }

    // Handle mouse move to detect hover over buttons
    _handleMouseMove(event) {
        // Convert screen coordinates to normalized device coordinates (-1 to 1)
        const x = (event.offsetX / event.target.clientWidth) * 2 - 1;
        const y = -(event.offsetY / event.target.clientHeight) * 2 + 1;
        this._checkButtonOver(x, y); // Check if mouse is over any button
    }

    // Check hover state and update button material colors accordingly
    _checkButtonOver(x, y) {
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera({ x, y }, this._camera); // Cast ray from camera through mouse position
        const intersects = raycaster.intersectObjects(this._buttons.children, true); // Check for intersections

        // Reset all buttons to default color (unhover)
        this._buttons.traverse(function (obj) {
            if (obj.name) {
                obj.material.color.setHex(0xffffff); // Default color
            }
        });

        // If mouse intersects with a button, change its color to indicate hover
        if (intersects.length) {
            for (let { object } of intersects) {
                if (object.name) {
                    object.parent.children.forEach(function (child) {
                        if (child.name === object.name) {
                            child.material.color.setHex(0xe2e2e2); // Hover color
                        }
                    });
                    break; // Exit after finding the first hovered button
                }
            }
        }
    }

    // Handle mouse click event to trigger zoom functionality
    _handleMouseClick(event) {
        // Convert screen coordinates to normalized device coordinates
        const x = (event.offsetX / event.target.clientWidth) * 2 - 1;
        const y = -(event.offsetY / event.target.clientHeight) * 2 + 1;
        this._checkButtonTouch(x, y); // Check if a button was clicked
    }

    // Check for button click and trigger appropriate zoom method
    _checkButtonTouch(x, y) {
        const raycaster = new THREE.Raycaster();
        raycaster.setFromCamera({ x, y }, this._camera); // Raycast from camera through mouse
        const intersects = raycaster.intersectObjects(this._buttons.children, true); // Check for intersections

        if (intersects.length) {
            for (let { object } of intersects) {
                if (object.name === "in") {
                    this._controls.zoomIn();  // Trigger zoom in
                } else if (object.name === "out") {
                    this._controls.zoomOut(); // Trigger zoom out
                }
            }
        }
    }

    // Expose the button object (Object3D) for adding to the scene externally
    getObject() {
        return this._buttons;
    }
}
